import type { SetupSection } from './fieldTypes';

const EMAIL = { regex: /^[^@\s]+@[^@\s]+\.[^@\s]+$/, message: 'Enter a valid email address' };

export const BANK_INVESTOR_SECTIONS: SetupSection[] = [
  {
    id: 'bank',
    group: 'FUND',
    title: 'Bank & payments',
    summary: 'The trust account that receives applications and pays distributions.',
    blocks: [
      {
        title: 'Trust bank account',
        fields: [
          { key: 'bank.name', label: 'Bank', kind: 'text', required: true },
          {
            key: 'bank.bsb',
            label: 'BSB',
            kind: 'text',
            required: true,
            pattern: { regex: /^\d{3}-?\d{3}$/, message: 'BSB is 6 digits' },
          },
          {
            key: 'bank.account',
            label: 'Account number',
            kind: 'text',
            required: true,
            pattern: { regex: /^\d{6,9}$/, message: '6–9 digits' },
          },
          {
            key: 'bank.accountName',
            label: 'Account name',
            kind: 'text',
            required: true,
            wide: true,
          },
        ],
      },
      {
        title: 'Receiving payments',
        fields: [{ key: 'bank.bpay', label: 'BPAY biller code', kind: 'text' }],
      },
      {
        title: 'Paying out (ABA files)',
        fields: [
          {
            key: 'bank.apca',
            label: 'APCA user ID',
            kind: 'text',
            help: 'Only needed for ABA files, which come in phase 2.',
            pattern: { regex: /^\d{6}$/, message: '6 digits' },
          },
          {
            key: 'bank.selfBalancing',
            label: 'Add a self-balancing row to ABA files',
            kind: 'switch',
            help: 'Some banks require a balancing debit line in each file.',
          },
        ],
      },
    ],
  },
  {
    id: 'investors',
    group: 'FUND',
    title: 'Investor rules',
    summary: 'Wholesale tests, identity checks and protections on bank detail changes.',
    blocks: [
      {
        title: 'Wholesale status',
        fields: [
          {
            key: 'inv.wholesaleMode',
            label: 'How wholesale status is determined',
            kind: 'select',
            wide: true,
            options: [
              { value: 'ANY_CONTACT', label: 'Any one contact on the investor qualifies' },
              { value: 'PER_INVESTOR_TYPE', label: 'All required contacts, by investor type' },
              { value: 'MANUAL', label: 'Set manually by staff (with override)' },
            ],
            toConfirm: true,
          },
        ],
      },
      {
        title: 'Identity verification',
        fields: [
          {
            key: 'inv.verification',
            label: 'Verification',
            kind: 'select',
            wide: true,
            options: [{ value: 'MANUAL', label: 'Manual documents only' }],
            help: 'Staff review an identity document for each person, and a deed or constitution for entities.',
          },
          {
            key: 'inv.entityDocs',
            label: 'Ask for trust deeds and company constitutions',
            kind: 'switch',
            wide: true,
            help: 'Required for entities in phase 1.',
          },
        ],
      },
      {
        title: 'Bank account changes',
        fields: [
          {
            key: 'inv.investorBankChange',
            label: 'Changes by investors',
            kind: 'select',
            options: [
              { value: 'REQUIRE_STATEMENT', label: 'Bank statement and staff approval' },
              { value: 'BLOCK', label: 'Block (staff only)' },
            ],
          },
          {
            key: 'inv.staffBankChange',
            label: 'Changes by staff',
            kind: 'select',
            options: [
              { value: 'REQUIRE_STATEMENT', label: 'Bank statement and staff approval' },
              { value: 'BLOCK', label: 'Block' },
            ],
          },
          {
            key: 'inv.alertEmail',
            label: 'Alert email for bank changes',
            kind: 'text',
            pattern: EMAIL,
          },
        ],
      },
      {
        title: 'Access and certificates',
        fields: [
          { key: 'inv.suspendAccess', label: 'Suspend investor access', kind: 'switch' },
          {
            key: 'inv.nameCase',
            label: 'Names on certificates',
            kind: 'select',
            options: [
              { value: 'AS_ENTERED', label: 'As entered' },
              { value: 'TITLE', label: 'Title Case' },
              { value: 'UPPER', label: 'UPPER CASE' },
            ],
          },
        ],
      },
    ],
  },
];
